import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useCreateAccountGSTDetailsMutation,
  useCreateAccountMasterMutation,
  useCreateBankDetailsMutation,
  useGetAccountGSTDetailsDataQuery,
  useGetBankDetailsByAccountNameQuery,
  useListAccountMasterSearchQuery,
  useUpdateAccountGSTDetailsMutation,
  useUpdateAccountMasterMutation,
} from '../api/accountMaster.api';
import { showErrorSnackbar, showSuccessSnackbar, showWarningSnackbar } from '../../../components/snackBar/snakbar';
import { useDispatch, useSelector } from 'react-redux';
import { dashboardApi } from '../../../redux/apis/dashboardApi';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import { IfscFetch } from '../../../library/IfscFetch';
import { listAccountOpeningBalanceApi } from '../../../redux/apis/accountOpeningBalance.api';
import isEqual from 'lodash.isequal';
import { listAccountBalanceApi } from '../../../redux/apis/listAccountBalance.api';
import { fieldLabels, INITIAL_STATE, InitialStateErrorType, InitialStateType, map, getTaxabilityOptions } from '../constants/StaticData';
import { CreateLedgerFormProps } from '../types';
import { CreateAccountGSTDetailsInput, UpdateAccountGSTDetailsInput } from '../../../graphql';
import { listAccountMasterv2Api } from '../../../redux/apis/accountMaster.api';

const INITIAL_ERRORS = Object.keys(INITIAL_STATE).reduce((acc, key) => ({ ...acc, [key]: '' }), {}) as InitialStateErrorType;

const useLedgerModal = ({
  groupId,
  accountId,
  title,
  onSave,
  onClose,
  cx,
  selectedGroupType,
  setselectedGroupType,
  options,
  updateCurrentLedgerName,
}: CreateLedgerFormProps & { updateCurrentLedgerName?: (ledgerName: string) => void }) => {
  title = title || '';
  const dispatch = useDispatch();
  const lastAutoNameRef = useRef<string>('');

  const [formData, setFormData] = useState<InitialStateType>({
    ...INITIAL_STATE,
    selectedGroup: groupId,
    ledgerName: String(title || ''),
    ledgerNameOrigin: String(title || '').trim() ? 'user' : 'auto',
    isEdit: !!accountId && !!title,
  });
  const [originalFormData, setOriginalFormData] = useState({
    ...INITIAL_STATE,
    selectedGroup: groupId,
    ledgerName: title,
    isEdit: !!accountId && !!title,
  });
  const [errors, setErrors] = useState<InitialStateErrorType>(INITIAL_ERRORS);
  const [expandGST, setExpandGST] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [existingLedgers, setExistingLedgers] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [backdropOpen, setBackdropOpen] = useState(false);

  const companyDetails = useSelector((state: any) => state?.companyReducer?.companyDetails);
  const companyID = companyDetails?.companyid;
  const companyType = companyDetails?.relatedcompanydetailsnew?.[0]?.registrationtype;
  const isCompanyUnRegistrationType = companyType === 1;
  
  const gstCompensationCessFlag = companyDetails?.relatedcompanydetailsnew?.[0]?.gstcompensationcessflag;
  
  const selectedGroupOption = options?.find((opt) => opt.value === formData.selectedGroup);
  const selectedGroupPath = selectedGroupOption?.path || '';
  
  const isDirectSpecialGroup = [18, 22, 24].includes(Number(formData.selectedGroup));
  const isSubgroupUnderSpecialGroup = ['/18/', '/22/', '/24/'].some(path => selectedGroupPath.includes(path));
  const isSpecialGroup = isDirectSpecialGroup || isSubgroupUnderSpecialGroup;
  
  const shouldShowGSTTypeAndCess = isSpecialGroup ? gstCompensationCessFlag === 1 : true;

  const { data: accountMasterSearch = [] } = useListAccountMasterSearchQuery({ word: searchTerm }, { skip: !searchTerm });
  const { data: bankDetailsData = [] } = useGetBankDetailsByAccountNameQuery(
    { accountname: title },
    { skip: !(selectedGroupType?.type === 'bank' && title) }
  );

  const { data: accountGstDetails } = useGetAccountGSTDetailsDataQuery(
    { accountid: accountId || 0 },
    { skip: !(selectedGroupType?.type === 'account' && accountId) }
  );

  const [createAccountMaster, { isLoading: isCreating }] = useCreateAccountMasterMutation();
  const [updateAccountMaster] = useUpdateAccountMasterMutation();
  const [createAccountGSTDetails] = useCreateAccountGSTDetailsMutation();
  const [updateAccountGSTDetails] = useUpdateAccountGSTDetailsMutation();
  const [createBankDetails] = useCreateBankDetailsMutation();

  useEffect(() => {
    if (!accountMasterSearch.length) return;
    const lc = formData.ledgerName.trim().toLowerCase();
    if (accountMasterSearch.some((a) => a?.accountname?.toLowerCase() === lc && a?.accountname?.toLowerCase() !== title?.toLowerCase())) {
      setErrors((e) => ({ ...e, ledgerName: 'Name already exists' }));
    } else {
      setErrors((e) => ({ ...e, ledgerName: '' }));
    }
    setExistingLedgers(
      accountMasterSearch
        .filter((a) => a?.accountname?.toLowerCase()?.includes?.(lc) && a?.accountname?.toLowerCase?.() !== title?.toLowerCase())
        .slice(0, 5)
        .map((a) => a?.accountname || '')
    );
  }, [accountMasterSearch, formData.ledgerName, title]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's' && (isDirty || isCreating)) {
        e.preventDefault();
        handleSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [formData, isDirty, isCreating]);

  useEffect(() => {
    if (!originalFormData) return;
    const cleanedCurrent = JSON.parse(JSON.stringify(formData));
    delete cleanedCurrent.isDirty;
    const cleanedOriginal = JSON.parse(JSON.stringify(originalFormData));
    delete cleanedOriginal.isDirty;
    setIsDirty(!isEqual(cleanedCurrent, cleanedOriginal));
  }, [formData, originalFormData]);

  // Update the current ledger name in the modal state when it changes
  useEffect(() => {
    if (updateCurrentLedgerName && formData.ledgerName) {
      updateCurrentLedgerName(formData.ledgerName);
    }
  }, [formData.ledgerName, updateCurrentLedgerName]);

  useEffect(() => {
    if (bankDetailsData?.length) {
      const b = bankDetailsData[bankDetailsData.length - 1];
      setFormData((fd) => ({
        ...fd,
        bankName: b?.bankname || '',
        branch: b?.branch || '',
        ifscCode: b?.ifsccode || '',
        accountNumber: b?.bankaccountnumber || '',
        swiftCode: b?.swiftcode || '',
        accountType: b?.bankaccounttype?.toString() || '',
      }));
      setOriginalFormData((fd) => ({
        ...fd,
        bankName: b?.bankname || '',
        branch: b?.branch || '',
        ifscCode: b?.ifsccode || '',
        accountNumber: b?.bankaccountnumber || '',
        swiftCode: b?.swiftcode || '',
        accountType: b?.bankaccounttype?.toString() || '',
      }));
    }
  }, [bankDetailsData]);

  useEffect(() => {
    if (accountGstDetails?.length) {
      const g = accountGstDetails[0];
      setFormData((fd) => ({
        ...fd,
        cessType: g?.gstcompensationtype || 'Amount',
        cessValue: Number(g?.cessamount),
        gstRate: g?.gstrate || 0,
        hsnId: g?.hsnid ? String(g?.hsnid) : '',
        taxability: g?.taxability ? Number(g?.taxability) : null,
      }));
      setOriginalFormData((fd) => ({
        ...fd,
        hsnId: g?.hsnid ? String(g?.hsnid) : '',
        gstRate: g?.gstrate || 0,
        cessType: g?.gstcompensationtype || 'Amount',
        cessValue: g?.cessamount || 0,
        taxability: g?.taxability ? Number(g?.taxability) : null,
      }));
      setExpandGST(true);
    } 
  }, [accountGstDetails]);

 const buildAutoLedgerName = (bankName?: string, branch?: string, accNo?: string) => {
  const last4 = (accNo || '').slice(-4);
  return `${(bankName || '').trim()} ${(branch || '').trim()} ${last4}`
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 50);
};

const isBankAutoReady = (bankName?: string, branch?: string, accNo?: string) => {
  const bn = (bankName || '').trim();
  const br = (branch || '').trim();
  const an = (accNo || '').replace(/\D/g, ''); // digits only
  return bn.length >= 3 && br.length >= 2 && /^\d{9,18}$/.test(an);
};


const applyAutoName = useMemo(
  () =>
    debounce((auto: string) => {
      setFormData(fd => {
        if (fd.ledgerNameOrigin === 'user') return fd;
        if (fd.ledgerName && fd.ledgerName !== lastAutoNameRef.current) return fd;

        lastAutoNameRef.current = auto;
        return {
          ...fd,
          ledgerName: auto,
          ledgerNameOrigin: 'auto',
          isDirty: { ...fd.isDirty, ledgerName: true },
        };
      });
      setErrors(err => ({ ...err, ledgerName: '' }));
    }, 400),
  []
);

useEffect(() => () => applyAutoName.cancel(), [applyAutoName]);

useEffect(() => {
  if (selectedGroupType?.type !== 'bank') return;

  const { bankName, branch, accountNumber } = formData;
  if (!isBankAutoReady(bankName, branch, accountNumber)) return;

  const auto = buildAutoLedgerName(bankName, branch, accountNumber);
  applyAutoName(auto);
}, [
  selectedGroupType?.type,
  formData.bankName,
  formData.branch,
  formData.accountNumber,
  applyAutoName,
]);



const maybeForceAutofill = () => {
  const { bankName, branch, accountNumber, ledgerNameOrigin, ledgerName } = formData;
  if (
    selectedGroupType?.type === 'bank' &&
    ledgerNameOrigin !== 'user' &&
    isBankAutoReady(bankName, branch, accountNumber) &&
    (!ledgerName || ledgerName === lastAutoNameRef.current)
  ) {
    const auto = buildAutoLedgerName(bankName, branch, accountNumber);
    lastAutoNameRef.current = auto;
    setFormData(fd => ({ ...fd, ledgerName: auto, ledgerNameOrigin: 'auto', isDirty: { ...fd.isDirty, ledgerName: true } }));
    setErrors(err => ({ ...err, ledgerName: '' }));
  }
};


  const handleGroupType = (groupId: number) => {
    const sel = options.find((g) => g.value === groupId);
    if (!sel) return;
    const path = sel.path;
    setselectedGroupType({
      groupId: groupId,
      type: map[Object.keys(map)?.find((k) => String(path).includes(k)) ?? ''] || 'account',
      accounttypecd: sel?.accounttypecd || '',
    });
  };

  const debounced = useMemo(() => debounce((text: string) => setSearchTerm(text.trim()), 400), []);

  const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

  const formatName = (value: string) => value.replace(/\s+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | { target: { name: string; value: string; type?: string | null } }
  ) => {
    const { name, value, type } = event.target;
    if (name === 'ledgerName') {
      const typed = (value ?? '').toString();
      debounced(formatName(typed));
      const formatted = formatName(typed);
      setFormData((fd) => ({
        ...fd,
        [name]: formatted,
        ledgerNameOrigin: formatted.length === 0 ? 'auto' : 'user',
        isDirty: {
          ...fd.isDirty,
          ledgerName: true,
        },
      }));
      setErrors((err) => ({ ...err, [name]: '' }));
    } else if (name === 'selectedGroup') {
      setFormData((prev) => ({ ...prev, selectedGroup: Number(value), isDirty: { ...prev.isDirty, ledgerName: true } }));
      setErrors((prev) => ({ ...prev, selectedGroup: '' }));
      handleGroupType(Number(value));
    } else if (['ifscCode', 'bankName', 'branch', 'accountNumber', 'swiftCode', 'accountType'].includes(name)) {
      const fieldValue = ['bankName', 'branch', 'swiftCode'].includes(name) ? formatName(value) : value;
      setFormData((fd) => ({
        ...fd,
        [name]: fieldValue,
        isDirty: {
          ...fd.isDirty,
          bankDetails: true,
          gstDetails: false,
        },
      }));
      setErrors((err) => ({ ...err, [name]: '' }));

      if (name === 'ifscCode' && IFSC_REGEX.test(fieldValue)) {
        setBackdropOpen(true);
        IfscFetch(fieldValue)
          .then((resp) => {
            const eData = resp.data;
            setFormData((fd) => ({
              ...fd,
              bankName: eData.bankName,
              branch: eData.branchName,
              swiftCode: eData.swift,
            }));
            setErrors((err) => ({
              ...err,
              ifscCode: '',
              bankName: '',
              branch: '',
            }));
            setBackdropOpen(false);
          })
          .catch(() => {
            showWarningSnackbar('IFSC not found in our database');
            setErrors((err) => ({ ...err, ifscCode: 'Invalid IFSC or not found' }));
            setBackdropOpen(false);
          });
      }
    } else if (['hsnId', 'taxability', 'gstRate', 'cessType', 'cessValue'].includes(name)) {
      setFormData((fd) => ({
        ...fd,
        [name]: type === 'number' ? Number(value) : value,
        isDirty: {
          ...fd.isDirty,
          gstDetails: true,
          bankDetails: false,
        },
      }));
      setErrors((err) => ({ ...err, [name]: '' }));
    } else {
      setFormData((fd) => ({ ...fd, [name]: value }));
      setErrors((err) => ({ ...err, [name]: '' }));
    }
  };

  // Helper function to check if API response has errors
  const checkApiResponseError = (response: any, responseKey: string): { hasError: boolean; errorMessage?: string } => {
    if (!response) {
      return { hasError: false };
    }

    const data = response[responseKey];
    if (!data) {
      return { hasError: false };
    }
    
    // Check for error status in response (primary check)
    if (data?.statuscode === 'Error' || data?.statuscode === 'ERROR') {
      const errorMessage = data?.statusmessage || data?.statusMessage || 'An error occurred';
      return { hasError: true, errorMessage };
    }

    // For account master operations, also check if accountsid is 0 or invalid (secondary check)
    // This handles cases where statuscode might not be set but the operation failed
    if (responseKey === 'updateAccountMaster' || responseKey === 'createAccountMaster') {
      // Only check accountsid if statuscode is not explicitly "Success" or similar
      const statusCode = data?.statuscode?.toLowerCase();
      if (statusCode !== 'success' && statusCode !== 'ok') {
        if (data?.accountsid === 0 || data?.accountsid === null || data?.accountsid === undefined) {
          const errorMessage = data?.statusmessage || data?.statusMessage || 'Failed to save account';
          return { hasError: true, errorMessage };
        }
      }
    }

    return { hasError: false };
  };

  const validateForm = () => {
    const newErr = { ...errors };
    type ValidFields = 'ledgerName' | 'selectedGroup';

    const requiredFields: ValidFields[] = ['ledgerName', 'selectedGroup'];

    requiredFields.forEach((f) => {
      const value = formData[f];
      if (value === null || value?.toString().trim() === '') {
        newErr[f] = `${fieldLabels[f]} is required`;
      }
    });

    if (selectedGroupType?.type === 'bank') {
      const bankFields: Array<'ifscCode' | 'bankName' | 'branch' | 'accountNumber' | 'accountType'> = [
        'ifscCode',
        'bankName',
        'branch',
        'accountNumber',
        'accountType',
      ];

      const hasAnyBankField = bankFields.some((f) => formData[f]?.toString().trim());

      if (hasAnyBankField) {
        bankFields.forEach((f) => {
          if (!formData[f]?.toString().trim()) {
            newErr[f] = `${fieldLabels[f]} is required`;
          }
        });
      }

      if (formData.ifscCode?.trim() && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode.trim())) {
        newErr.ifscCode = 'Invalid IFSC format';
      }
      if (formData.accountNumber?.trim() && !/^\d{9,18}$/.test(formData.accountNumber.trim())) {
        newErr.accountNumber = 'Account number must be 9–18 digits';
      }
    }

    // const gstFields: Array<'hsnId' | 'gstRate' | 'cessValue'> = ['hsnId', 'gstRate', 'cessValue'];

    // if (gstFields.some((f) => formData[f]?.toString().trim())) {
    //   gstFields.forEach((f) => {
    //     if (f === 'cessValue') {
    //       if (!formData[f] && formData[f] !== 0) {
    //         newErr[f] = `${fieldLabels[f]} is required when providing GST details`;
    //       }
    //     } else if (!formData[f]?.toString().trim()) {
    //       newErr[f] = `${fieldLabels[f]} is required when providing GST details`;
    //     }
    //   });
    // }

    setErrors(newErr);
    return Object.values(newErr).every((errMsg) => !errMsg);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      // showErrorSnackbar('Please fix validation errors');
      return;
    }
    setBackdropOpen(true);
    let accSid = 0;
    let allOperationsSuccessful = true;
    let errorMessage = '';
    try {
      if (!formData.selectedGroup) {
        setErrors((prev) => ({ ...prev, selectedGroup: 'Group is required' }));
        setBackdropOpen(false);
        return;
      }

      // Handle update account master
      if (formData.isEdit && formData?.isDirty?.ledgerName) {
        if (accountId && formData.selectedGroup) {
          try {
            const updateRes = await updateAccountMaster({
              updateAccountMasterInput: {
                accountsid: accountId,
                accountname: formData.ledgerName.trim(),
                accountgroupid: formData.selectedGroup,
                accounttypecd: String(selectedGroupType?.accounttypecd),
                hsnsaccode: '',
                companybranchid: 0,
                accounttypeflag: 0,
                is_active: 1,
              },
            }).unwrap();

            const errorCheck = checkApiResponseError(updateRes, 'updateAccountMaster');
            if (errorCheck.hasError) {
              allOperationsSuccessful = false;
              errorMessage = errorCheck.errorMessage || 'Failed to update account';
              setErrors((prev) => ({ ...prev, ledgerName: errorCheck.errorMessage || 'Account update failed' }));
              throw new Error(errorCheck.errorMessage || 'Failed to update account');
            }
          } catch (error: any) {
            // If error is already set above, re-throw it
            if (errorMessage) {
              throw error;
            }
            // Otherwise handle the error
            const errorMsg = error?.data?.updateAccountMaster?.statusmessage || 
                           error?.message || 
                           'Failed to update account';
            allOperationsSuccessful = false;
            errorMessage = errorMsg;
            setErrors((prev) => ({ ...prev, ledgerName: errorMsg }));
            throw new Error(errorMsg);
          }
        }
      }

      // Handle update/create GST details for edit mode
      if (formData.isEdit && selectedGroupType?.type === 'account' && formData.isDirty?.gstDetails && accountGstDetails && accountId) {
        const gstExists = accountGstDetails?.length > 0;
        const gstUpdatePayload: UpdateAccountGSTDetailsInput = {
          accountid: accountId,
          effectivedate: gstExists && accountGstDetails?.[0]?.effectivedate ? accountGstDetails?.[0]?.effectivedate : dayjs().unix(),
          expireddate: gstExists && accountGstDetails?.[0]?.expireddate ? accountGstDetails?.[0]?.expireddate : 0,
          gstrate: formData.gstRate || 0,
          hsnid: formData.hsnId ? String(formData.hsnId) : '',
          gstcompensationtype: formData.cessType,
          cessamount: Number(formData.cessValue),
          taxability: formData.taxability ? String(formData.taxability) : '',
          enteredtimestamp: 0,
        };
        try {
          if (gstExists) {
            gstUpdatePayload.enteredtimestamp = dayjs().unix();
            const gstUpdateRes = await updateAccountGSTDetails({ updateAccountGSTDetailsInput: gstUpdatePayload }).unwrap();
            const errorCheck = checkApiResponseError(gstUpdateRes, 'updateAccountGSTDetails');
            if (errorCheck.hasError) {
              allOperationsSuccessful = false;
              errorMessage = errorCheck.errorMessage || 'Failed to update GST details';
              throw new Error(errorCheck.errorMessage || 'Failed to update GST details');
            }
          } else {
            const gstPayload: CreateAccountGSTDetailsInput = {
              accountid: accountId,
              effectivedate: gstExists && accountGstDetails?.[0]?.effectivedate ? accountGstDetails?.[0]?.effectivedate : dayjs().unix(),
              expireddate: gstExists && accountGstDetails?.[0]?.expireddate ? accountGstDetails?.[0]?.expireddate : 0,
              gstrate: formData.gstRate || 0,
              hsnid: formData.hsnId ? String(formData.hsnId) : '',
              gstcompensationtype: formData.cessType,
              cessamount: Number(formData.cessValue),
              taxability: formData.taxability ? String(formData.taxability) : '',
            };
            const gstCreateRes = await createAccountGSTDetails({ createAccountGSTDetailsInput: gstPayload }).unwrap();
            const errorCheck = checkApiResponseError(gstCreateRes, 'createAccountGSTDetails');
            if (errorCheck.hasError) {
              allOperationsSuccessful = false;
              errorMessage = errorCheck.errorMessage || 'Failed to create GST details';
              throw new Error(errorCheck.errorMessage || 'Failed to create GST details');
            }
          }
        } catch (error: any) {
          if (!errorMessage) {
            errorMessage = error?.data?.updateAccountGSTDetails?.statusmessage || 
                          error?.data?.createAccountGSTDetails?.statusmessage ||
                          error?.message || 
                          'Failed to save GST details';
          }
          allOperationsSuccessful = false;
          throw error;
        }
      }

      // Handle create account master
      if (!formData.isEdit) {
        try {
          const createRes = await createAccountMaster({
            createAccountMasterInput: {
              accountname: formData.ledgerName.trim(),
              accountgroupid: formData.selectedGroup,
              accounttypecd: selectedGroupType.accounttypecd,
              hsnsaccode: '',
            },
          }).unwrap();

          const errorCheck = checkApiResponseError(createRes, 'createAccountMaster');
          if (errorCheck.hasError) {
            allOperationsSuccessful = false;
            errorMessage = errorCheck.errorMessage || 'Failed to create account';
            setErrors((prev) => ({ ...prev, ledgerName: errorCheck.errorMessage || 'Account creation failed' }));
            throw new Error(errorCheck.errorMessage || 'Failed to create account');
          }

          accSid = Number(createRes?.createAccountMaster?.accountsid);
          if (!accSid || accSid === 0) {
            allOperationsSuccessful = false;
            errorMessage = 'Failed to create account: Invalid account ID';
            throw new Error('Failed to create account: Invalid account ID');
          }

          // Create GST details for new account
          if (selectedGroupType?.type === 'account' && formData.isDirty?.gstDetails) {
            try {
              const gstCreateRes = await createAccountGSTDetails({
                createAccountGSTDetailsInput: {
                  accountid: accSid,
                  effectivedate: dayjs().unix(),
                  expireddate: 0,
                  gstrate: formData.gstRate || 0,
                  hsnid: formData.hsnId ? String(formData.hsnId) : '',
                  gstcompensationtype: formData.cessType,
                  cessamount: Number(formData.cessValue),
                  taxability: formData.taxability ? String(formData.taxability) : '',
                },
              }).unwrap();
              const errorCheck = checkApiResponseError(gstCreateRes, 'createAccountGSTDetails');
              if (errorCheck.hasError) {
                allOperationsSuccessful = false;
                errorMessage = errorCheck.errorMessage || 'Failed to create GST details';
                throw new Error(errorCheck.errorMessage || 'Failed to create GST details');
              }
            } catch (error: any) {
              if (!errorMessage) {
                errorMessage = error?.data?.createAccountGSTDetails?.statusmessage || 
                              error?.message || 
                              'Failed to create GST details';
              }
              allOperationsSuccessful = false;
              throw error;
            }
          }
        } catch (error: any) {
          if (!errorMessage) {
            errorMessage = error?.data?.createAccountMaster?.statusmessage || 
                          error?.message || 
                          'Failed to create account';
          }
          allOperationsSuccessful = false;
          setErrors((prev) => ({ ...prev, ledgerName: errorMessage }));
          throw error;
        }
      } else {
        // For edit mode, use existing accountId
        accSid = accountId || 0;
      }

      // Handle bank details creation
      if (selectedGroupType?.type === 'bank' && formData.isDirty?.bankDetails) {
        try {
          const bankRes = await createBankDetails({
            createBankDetailsInput: [
              {
                bankdetailsid: Number(accSid),
                bankaccountnumber: formData.accountNumber,
                bankname: formData.bankName,
                branch: formData.branch,
                ifsccode: formData.ifscCode,
                swiftcode: formData.swiftCode,
                bankaccounttype: String(formData.accountType),
                ledgeraccount: formData.ledgerName,
                objectid: companyID.toString(),
                objecttype: 'COMPANY',
                notes: '',
                cash: 0,
                upi: '',
                companybranchid: 0,
                expiredtimestamp: 0,
              },
            ],
          }).unwrap();
          // Check for errors in bank details response if applicable
          if (bankRes?.createBankDetails?.[0]?.statuscode === 'Error' || bankRes?.createBankDetails?.[0]?.statuscode === 'ERROR') {
            const bankErrorMsg = bankRes?.createBankDetails?.[0]?.statusmessage || 'Failed to save bank details';
            allOperationsSuccessful = false;
            errorMessage = bankErrorMsg;
            throw new Error(bankErrorMsg);
          }
        } catch (error: any) {
          if (!errorMessage) {
            errorMessage = error?.data?.createBankDetails?.[0]?.statusmessage || 
                          error?.message || 
                          'Failed to save bank details';
          }
          allOperationsSuccessful = false;
          throw error;
        }
      }
      
      if (bankDetailsData?.length && selectedGroupType?.type !== 'bank') {
        try {
          await createBankDetails({
            createBankDetailsInput: [
              {
                bankdetailsid: Number(accSid),
                bankaccountnumber: '0',
                bankname: '',
                branch: '',
                ifsccode: '0',
                swiftcode: '',
                bankaccounttype: '',
                ledgeraccount: '',
                objectid: '0',
                objecttype: 'COMPANY',
                notes: '',
                cash: 0,
                upi: '',
                companybranchid: 0,
                expiredtimestamp: 0,
              },
            ],
          });
        } catch (error: any) {
          // This is a cleanup operation, log but don't fail the whole process
          console.warn('Failed to clear bank details:', error);
        }
      }

      // Only show success and proceed if all operations were successful
      if (allOperationsSuccessful && accSid > 0) {
        onSave({ cx, accountsId: accSid, groupId: formData.selectedGroup, isActive: 1, accountTypeCd: 'BS' });
        showSuccessSnackbar(formData.isEdit ? 'Ledger updated successfully' : 'Ledger created successfully');
        dispatch(dashboardApi.util.invalidateTags(['dashboard']));
        dispatch(listAccountMasterv2Api.util.invalidateTags(['listAccountMaster']));
        dispatch(listAccountOpeningBalanceApi.util.invalidateTags(['listAccountOpeningBalance']));
        dispatch(listAccountBalanceApi.util.invalidateTags(['listAccountBalance']));
        onClose({ cx });
      } else {
        throw new Error(errorMessage || 'Operation failed');
      }
    } catch (error: any) {
      const finalErrorMessage = errorMessage || error?.data?.updateAccountMaster?.statusmessage || 
                                error?.data?.createAccountMaster?.statusmessage ||
                                error?.data?.updateAccountGSTDetails?.statusmessage ||
                                error?.data?.createAccountGSTDetails?.statusmessage ||
                                error?.data?.createBankDetails?.[0]?.statusmessage ||
                                error?.message || 
                                'Error saving ledger';
      showErrorSnackbar(finalErrorMessage);
      
      // Set form error for ledger name if it's a duplicate name error
      if (finalErrorMessage.toLowerCase().includes('already exists') || 
          finalErrorMessage.toLowerCase().includes('name already')) {
        setErrors((prev) => ({ ...prev, ledgerName: finalErrorMessage }));
      }
    } finally {
      setBackdropOpen(false);
    }
  };

  return {
    formData,
    errors,
    options,
    expandGST,
    isCreating,
    existingLedgers,
    groupType: selectedGroupType?.type,
    backdropOpen,
    isDirty,
    isCompanyUnRegistrationType,
    accountGstDetails,
    companyType,
    taxabilityOptions: getTaxabilityOptions(companyType || 1),
    shouldShowGSTTypeAndCess,
    setExpandGST,
    handleChange,
    handleSubmit,
    maybeForceAutofill,
  };
};

export default useLedgerModal;
